<template>
    <div class="w-full h-full">
        <form v-if="emailAddresses.length > 0" @submit.prevent="submitForm" class="p-2 flex flex-col gap-4">
            <div class="flex flex-col w-full">
                <label class="whitespace-nowrap tracking-wide font-semibold pr-1 text-gray-800">
                    Email Address:
                </label>
                <select v-model="selectedEmail" required class="border border-gray-300 outline-none hover:border-gray-400 focus:border-indigo-400 h-9 w-full">
                    <option disabled value="">Select an email address</option>
                    <option v-for="email in emailAddresses" :key="email.id" :value="email.email_address">
                        {{ email.email_address }} ({{ email.description }})
                    </option>
                </select>
            </div>
            <template v-if="selectedEmail">
                <div class="flex flex-col w-full">
                    <label class="whitespace-nowrap tracking-wide font-semibold pr-1 text-gray-800">
                        Username:
                    </label>
                    <input type="text" required placeholder="j_smith" autocomplete="off" class="p-1 border h-9 w-full" v-model="username" />
                </div>
                <div class="flex flex-col w-full">
                    <label class="whitespace-nowrap tracking-wide font-semibold pr-1 text-gray-800">
                        Password:
                    </label>
                    <div class="relative w-full">
                        <input :type="showPassword ? 'text' : 'password'" required placeholder="Password" autocomplete="new-password" class="p-1 border h-9 w-full pr-9" v-model="password" />
                        <button type="button" @click="showPassword = !showPassword" class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                            <svg v-if="showPassword" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9-4-9-7s4-7 9-7a10.05 10.05 0 011.875.175M15 12a3 3 0 11-6 0 3 3 0 016 0zm4.5-4.5l-9 9" />
                                <path stroke-linecap="round" stroke-linejoin="round" d="M3 3l18 18" />
                            </svg>
                            <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path stroke-linecap="round" stroke-linejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                        </button>
                    </div>
                    <ul v-if="password" class="mt-2 flex flex-col gap-0.5 text-xs">
                        <li class="flex items-center gap-1" :class="hasMinLength ? 'text-green-600' : 'text-red-500'">
                            <span>{{ hasMinLength ? '✓' : '✗' }}</span>
                            At least 8 characters
                        </li>
                        <li class="flex items-center gap-1" :class="!isAllNumeric ? 'text-green-600' : 'text-red-500'">
                            <span>{{ !isAllNumeric ? '✓' : '✗' }}</span>
                            Can't be entirely numeric
                        </li>
                        <li class="flex items-center gap-1 text-gray-500">
                            <span>–</span>
                            Can't be too similar to your personal information
                        </li>
                        <li class="flex items-center gap-1 text-gray-500">
                            <span>–</span>
                            Can't be a commonly used password
                        </li>
                    </ul>
                </div>
                <div class="flex flex-col w-full">
                    <label class="whitespace-nowrap tracking-wide font-semibold pr-1 text-gray-800">
                        Confirm Password:
                    </label>
                    <div class="relative w-full">
                        <input :type="showConfirmPassword ? 'text' : 'password'" required placeholder="Confirm Password" autocomplete="new-password" class="p-1 border h-9 w-full pr-9" :class="{'border-red-400': confirmPassword && password !== confirmPassword}" v-model="confirmPassword" />
                        <button type="button" @click="showConfirmPassword = !showConfirmPassword" class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                            <svg v-if="showConfirmPassword" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9-4-9-7s4-7 9-7a10.05 10.05 0 011.875.175M15 12a3 3 0 11-6 0 3 3 0 016 0zm4.5-4.5l-9 9" />
                                <path stroke-linecap="round" stroke-linejoin="round" d="M3 3l18 18" />
                            </svg>
                            <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path stroke-linecap="round" stroke-linejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                        </button>
                    </div>
                    <span v-if="confirmPassword && password !== confirmPassword" class="text-red-500 text-xs mt-1">Passwords do not match</span>
                </div>
            </template>
            <div class="flex items-center justify-center w-full">
                <base-button-affirmative v-if="selectedEmail" class="m-2" :disabled="submitting">
                    {{ submitting ? 'Creating...' : 'Create User' }}
                </base-button-affirmative>
                <base-button-disabled v-else class="m-2">Create User</base-button-disabled>
                <base-button-negative @click.prevent="emit('close')" class="m-2">Cancel</base-button-negative>
            </div>
        </form>
        <div v-else class="p-2 text-base">
            No email addresses associated with this person
        </div>
    </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import { adminCreateUserFromPerson } from '@/services/People';

const props = defineProps(['person'])
const emit = defineEmits(['close', 'refetch-people'])

const store = useStore()
const $toast = useToast()

const emailAddresses = computed(() => props.person.email_addresses)

const selectedEmail = ref('')
const username = ref('')
const password = ref('')
const confirmPassword = ref('')
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const submitting = ref(false)

const hasMinLength = computed(() => password.value.length >= 8)
const isAllNumeric = computed(() => /^\d+$/.test(password.value))
const passwordValid = computed(() => hasMinLength.value && !isAllNumeric.value)

async function submitForm() {
    if (password.value !== confirmPassword.value) return
    if (!passwordValid.value) return
    submitting.value = true
    const token = store.state.auth.token
    const data = {
        email: selectedEmail.value,
        username: username.value,
        password: password.value,
    }
    const result = await adminCreateUserFromPerson(token, data, props.person.id)
    submitting.value = false
    if (result.status === 201 || result.status === 200) {
        $toast.open({
            message: `Successfully created user ${username.value}`,
            type: 'success',
            duration: 3000,
        })
        emit('refetch-people')
        emit('close')
    } else {
        const detail = result.response?.data?.detail || result.response?.data?.username?.[0] || 'Could not create user'
        $toast.open({
            message: `Error: ${detail}`,
            type: 'error',
            duration: 4000,
        })
    }
}
</script>
